/* eslint-disable react/no-children-prop */
/* eslint-disable i18next/no-literal-string */
import React, { useState } from "react";

import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";

import {
  Box,
  Group,
  Card,
  CopyButton,
  AspectRatio,
  Fieldset,
  Flex,
  Title,
  Button,
} from "@mantine/core";

import { LargeTextTitleCard } from "../components/large-text-title-card";
import { StandardTitleCard } from "../components/standard-text-title-card";

import { StandardArticle } from "../components/standard-article-card";

import type {
  FeedItemDefinition,
  StandardFeedCard,
  LargeTextFeedCard,
  IterableInAppValues,
} from "@typings/parentapp-feed-types";

import { useAppForm } from "@components/form/app-form";

interface FormValues {
  //
  iterableValues: IterableInAppValues;
  //
  cardContentType: FeedItemDefinition["card"]["contentType"];
  standardCardContent: StandardFeedCard["content"];
  largeTextCardCommonContent: Omit<LargeTextFeedCard["content"], "background">;
  largeTextCardBackgroundType: LargeTextFeedCard["content"]["background"]["type"];
  largeTextCardBackgroundImg: NonNullable<
    LargeTextFeedCard["content"]["background"]["image"]
  >["url"];
  largeTextCardBackgroundColor: NonNullable<
    LargeTextFeedCard["content"]["background"]["color"]
  >;
  //
  hasArticle: boolean;
  articleContent: NonNullable<FeedItemDefinition["article"]>["content"] | null;
  //
  isAssociatedWithPlayer: boolean;
}

const defaults: FormValues = {
  //
  iterableValues: {
    mobileInboxTitle: "My Inbox Message",
    mobileInboxBody: 'This is the "body" for mobile inbox',
    mobileInboxIconURL:
      "https://img.icons8.com/?size=100&id=xxLnyTQEDJsr&format=png&color=000000",
  },
  //
  cardContentType: "STANDARD",
  standardCardContent: {
    image: {
      url: "https://dummyjson.com/image/200x150",
    },
  },
  largeTextCardBackgroundType: "COLOR",
  largeTextCardCommonContent: {
    textColor: "#ffffff",
  },
  largeTextCardBackgroundImg: "https://dummyjson.com/image/200x150",
  largeTextCardBackgroundColor: "#000000",
  hasArticle: true,
  articleContent: {
    image: {
      url: "https://dummyjson.com/image/200x150",
    },
    headerText: "Game Achievement",
  },
  isAssociatedWithPlayer: true,
};

export const CreateFeedItemPage = React.memo(function CreateFeedItemPage() {
  const [definition, setDefinition] = useState<FeedItemDefinition>();

  const form = useAppForm({
    defaultValues: defaults,
    onSubmit: ({ value }) => {
      console.log("submit", value);
      setDefinition({
        article: value.hasArticle
          ? {
              articleType: "STANDARD",
              content: {
                image: value.articleContent?.image ?? null,
                headerText: value.articleContent?.headerText ?? "",
              },
            }
          : null,
        card:
          value.cardContentType === "STANDARD"
            ? {
                contentType: value.cardContentType,
                content: value.standardCardContent,
              }
            : {
                contentType: value.cardContentType,
                content: {
                  ...value.largeTextCardCommonContent,
                  background:
                    value.largeTextCardBackgroundType === "COLOR"
                      ? {
                          type: "COLOR",
                          color: value.largeTextCardBackgroundColor,
                        }
                      : {
                          type: "IMAGE",
                          image: {
                            url: value.largeTextCardBackgroundImg,
                          },
                        },
                },
              },
        additionalContext: value.isAssociatedWithPlayer
          ? {
              playerId:
                "{{defaultIfEmpty playerId (defaultIfEmpty player_id player)}}",
            }
          : {},
      });
    },
    listeners: {
      onChange: ({ formApi, fieldApi }) => {
        // autosave logic
        if (formApi.state.isValid) {
          formApi.handleSubmit();
        }
      },
      // onChangeDebounceMs: 100,
      //
      //
      onMount: ({ formApi }) => {
        // set values in parent immediately
        if (formApi.state.isValid) {
          formApi.handleSubmit();
        }
      },
    },
  });

  console.log(definition?.card.content);

  return (
    <Group mt="xl" justify="space-between" align="flex-start">
      <Box flex={1} p="lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <Card withBorder>
            <Title order={4}>Iterable In-App Fields</Title>

            <form.AppField
              name="iterableValues.mobileInboxTitle"
              children={(field) => (
                <field.TextField
                  label="Mobile Inbox Title"
                  errors={field.state.meta.errors}
                />
              )}
            />

            <form.AppField
              name="iterableValues.mobileInboxBody"
              children={(field) => (
                <field.TextareaField
                  label="Mobile Inbox Body"
                  errors={field.state.meta.errors}
                />
              )}
            />

            <form.AppField
              name="iterableValues.mobileInboxIconURL"
              children={(field) => (
                <field.TextField
                  label="Mobile inbox thumbnail URL"
                  errors={field.state.meta.errors}
                />
              )}
            />

            <form.AppField
              name="isAssociatedWithPlayer"
              children={(field) => (
                <field.SwitchField label="Player-specific?" />
              )}
            />
          </Card>

          <Card withBorder mt="lg">
            <Title order={4}>Feed card (custom fields)</Title>

            <form.AppField
              name="cardContentType"
              children={(field) => (
                <field.SegmentedSelectField
                  data={["STANDARD", "LARGE_TEXT"] as const}
                  label="Card Type:"
                  value={field.state.value}
                  onChange={(newVal) => {
                    field.handleChange(newVal);
                  }}
                  onBlur={field.handleBlur}
                />
              )}
            />

            <form.Subscribe
              selector={(state) => state.values.cardContentType}
              children={(cardContentType) => {
                /**
                 * STANDARD FEED CARD
                 */
                if (cardContentType === "STANDARD") {
                  return (
                    <form.AppField
                      name="standardCardContent.image.url"
                      children={(field) => (
                        <field.TextField
                          label="Image URL"
                          placeholder="https://some-website/something.png"
                          errors={field.state.meta.errors}
                        />
                      )}
                    />
                  );
                }
                /**
                 * LARGE TEXT FEED CARD
                 */
                if (cardContentType === "LARGE_TEXT") {
                  return (
                    <>
                      <Fieldset mt="md" legend="Card text">
                        <form.AppField
                          name="largeTextCardCommonContent.textColor"
                          children={(field) => (
                            <field.ColorPickerField
                              label="Text Color"
                              value={field.state.value}
                              onChange={field.handleChange}
                            />
                          )}
                        />
                      </Fieldset>

                      <Fieldset mt="md" legend="Card background">
                        <form.AppField
                          name="largeTextCardBackgroundType"
                          children={(field) => (
                            <field.SegmentedSelectField
                              data={["COLOR", "IMAGE"] as const}
                              value={field.state.value}
                              onChange={(newVal) => field.handleChange(newVal)}
                              onBlur={field.handleBlur}
                              label="Background Type:"
                            />
                          )}
                        />

                        <form.Subscribe
                          selector={(state) =>
                            state.values.largeTextCardBackgroundType
                          }
                          children={(bgType) =>
                            bgType === "COLOR" ? (
                              <form.AppField
                                name="largeTextCardBackgroundColor"
                                children={(field) => (
                                  <field.ColorPickerField
                                    label="Background Color"
                                    value={field.state.value}
                                    onChange={field.handleChange}
                                  />
                                )}
                              />
                            ) : (
                              <form.AppField
                                name="largeTextCardBackgroundImg"
                                children={(field) => (
                                  <field.TextField
                                    label="Background Image URL"
                                    errors={field.state.meta.errors}
                                  />
                                )}
                              />
                            )
                          }
                        />
                      </Fieldset>
                    </>
                  );
                }

                return null;
              }}
            />
          </Card>

          <Card withBorder mt="md">
            <Title order={4}>Article View</Title>

            <form.AppField
              name="hasArticle"
              children={(field) => <field.SwitchField label="Has article?" />}
            />

            <form.Subscribe
              selector={(state) => state.values.hasArticle}
              children={(hasArticle) => {
                if (!hasArticle) {
                  return null;
                }
                return (
                  <>
                    <form.AppField
                      name="articleContent.image.url"
                      children={(field) => (
                        <field.TextField
                          label="Image URL"
                          errors={field.state.meta.errors}
                        />
                      )}
                    />
                    <form.AppField
                      name="articleContent.headerText"
                      children={(field) => (
                        <field.TextField
                          label="Header text"
                          errors={field.state.meta.errors}
                        />
                      )}
                    />
                  </>
                );
              }}
            />
          </Card>
        </form>

        <Card withBorder>
          <Box maw={600}>
            <SyntaxHighlighter
              language="json"
              style={docco}
              wrapLines
              customStyle={{ width: "100%" }}
            >
              {JSON.stringify(definition, null, 2)}
            </SyntaxHighlighter>
          </Box>

          <Box mt="lg">
            <CopyButton value={JSON.stringify(definition)}>
              {({ copied, copy }) => (
                <Button
                  color={copied ? "teal" : "blue"}
                  onClick={copy}
                  size="sm"
                >
                  {copied ? "Copied JSON" : 'Copy Iterable "Raw Data (JSON)"'}
                </Button>
              )}
            </CopyButton>
          </Box>
        </Card>
      </Box>

      <Box pt="xl" flex={2}>
        <Flex wrap="wrap" gap="md">
          <AspectRatio ratio={1179 / 2556} w={400} bg="black" p="sm">
            <Box>
              {definition?.card.contentType === "STANDARD" ? (
                <StandardTitleCard
                  content={definition.card.content}
                  iterableValues={form.getFieldValue("iterableValues")}
                  hasArticle={definition.article !== null}
                  isAssociatedWithPlayer={form.getFieldValue(
                    "isAssociatedWithPlayer",
                  )}
                />
              ) : null}

              {definition?.card.contentType === "LARGE_TEXT" ? (
                <LargeTextTitleCard
                  content={definition.card.content}
                  iterableValues={form.getFieldValue("iterableValues")}
                  hasArticle={definition.article !== null}
                  isAssociatedWithPlayer={form.getFieldValue(
                    "isAssociatedWithPlayer",
                  )}
                />
              ) : null}
            </Box>
          </AspectRatio>

          <form.Subscribe
            selector={(f) => f.values.hasArticle}
            children={(hasArticle) => {
              return hasArticle ? (
                <AspectRatio
                  ratio={1179 / 2556}
                  w={400}
                  bg="white"
                  bd="1px solid black"
                >
                  {definition?.article?.articleType === "STANDARD" ? (
                    <StandardArticle
                      content={definition.article.content}
                      isAssociatedWithPlayer={form.getFieldValue(
                        "isAssociatedWithPlayer",
                      )}
                    />
                  ) : null}
                </AspectRatio>
              ) : null;
            }}
          />
        </Flex>
      </Box>
    </Group>
  );
});
